import FourFiveColumnContent from '@/features/shared/FourFiveColumnContent';
import { MuiButtonBase } from '@/features/shared/material-ui';
import SwiperCarousel from '@/features/shared/SwiperCarousel';
import { colorMap, Theme } from '@/utils/constants';
import clsx from 'clsx';
import React, { memo, useRef } from 'react';
import { SwiperSlide } from 'swiper/react';
import ContentCard from './ContentCard';
import { ContentCardCarouselProps } from './ContentCardCarousel.interface';
import useStyles from './ContentCardCarousel.style';
import ContentCardSmall from './ContentCardSmall';
import SingleContentCard from './SingleContentCard/SingleContentCard';
import {
  useContentCardCarousel,
  useContentCarouselModel,
} from '@/features/CardCarousel/variants/ContentCardCarousel/ContentCardCarousel.logic';
import useIsMobile from '@/features/shared/Hooks/useIsMobile';

const ContentCardCarousel = ({
  content,
  cardColor = colorMap.charcoal,
  cardTheme = Theme.dark,
  theme,
  contentButtons,
  hideCTA,
  hideNavigation,
  mobileStacked,
}: Br.PropsWithContentButtons<ContentCardCarouselProps>) => {
  const classes = useStyles({});
  const rootClassName = clsx(classes.root, `${theme}-theme`);
  const isMobile = useIsMobile();

  const { contentList, useSwiper, cta } = useContentCardCarousel(content, mobileStacked);
  const { rootRef, slider, getIndex, setIndex, triggerCardContentClick, configuration } =
    useContentCarouselModel(contentList, useSwiper, classes);

  return (
    <div
      className={rootClassName}
      ref={rootRef}
      {...(!useSwiper && {
        'data-datalayer-ignore-card-carousel': true,
        'data-datalayer-position': 'static_card',
      })}
    >
      {contentButtons?.content}
      {useSwiper ? (
        <SwiperCarousel
          theme={theme}
          mdTheme={theme}
          cta={!hideCTA ? cta : null}
          navigation={!hideNavigation}
          slider={slider}
          classes={classes}
          {...configuration}
        >
          {contentList.map((card, idx: number) => (
            <SwiperSlide
              // eslint-disable-next-line react/no-array-index-key
              key={`card_${idx}`}
              className={clsx(classes.sliderCard, idx === 1 && classes.sliderActiveCard)}
              data-datalayer-card-index={idx}
              data-datalayer-card-title={card.cardHeading}
            >
              {(idx === 1 || isMobile) && (
                <ContentCard
                  index={getIndex(idx)}
                  data={!isMobile ? contentList : [card]}
                  theme={cardTheme}
                  cardColor={cardColor}
                />
              )}

              {idx !== 1 && !isMobile && (
                <MuiButtonBase
                  onClick={() => {
                    triggerCardContentClick.current = true;
                    setIndex(idx);
                  }}
                >
                  <ContentCardSmall
                    index={getIndex(idx)}
                    initialIndex={idx}
                    data={contentList}
                    theme={cardTheme}
                    cardColor={cardColor}
                  />
                </MuiButtonBase>
              )}
            </SwiperSlide>
          ))}
        </SwiperCarousel>
      ) : (
        <FourFiveColumnContent
          columnCount={contentList.length}
          columns={contentList.map((card, idx: number) => (
            <SingleContentCard
              // eslint-disable-next-line react/no-array-index-key
              key={`card_${idx}`}
              theme={cardTheme}
              cardColor={cardColor}
              data={card}
            />
          ))}
        />
      )}
    </div>
  );
};

export default memo(ContentCardCarousel);
